import {UntypedFormGroup} from '@angular/forms';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';

import {Expression, SearchQuery} from '../fiql/fiql.service';

type FilterFunction = (nameFilter: string,
                       valueFilter: string,
                       actualOperator: string,
                       actualValue: string | Array<Expression>) =>
  { actualOperator: string, actualValue: string | Array<Expression>, nameFilter: string };

export const updateFiltersOnGoingPayrolls = (filters: SearchQuery,
                                             nameFilter: string,
                                             valueFilter: string,
                                             operator: string) => {
  const allowedChars = /^[a-zA-Z0-9'\s]*$/;
  const listFunctions = [idFn, payStateStatusToOngoingPayrollsFn, retroActivityCalculationCountFn, nameFn, actionToOngoingPayrollsFn];
  updateFilters(valueFilter, filters, nameFilter, operator, listFunctions, allowedChars);
};

export const updateFiltersValidationPayrolls = (filters: SearchQuery,
                                                nameFilter: string,
                                                valueFilter: string,
                                                operator: string) => {
  const allowedChars = /^[a-zA-Z0-9'_\s]*$/;
  updateFilters(valueFilter, filters, nameFilter, operator, [nameFn, payStateStatusToValidationPayslipsFn], allowedChars);
};
export const updateFiltersPrepareDetails = (filters: SearchQuery,
                                            nameFilter: string,
                                            valueFilter: string,
                                            operator: string): boolean => {
  return updateFilters(valueFilter, filters, nameFilter, operator, [nameFn]);
};

const retroActivityCalculationCountFn = (nameFilter: string,
                                         valueFilter: string,
                                         actualOperator: string,
                                         actualValue: string | Array<Expression>) => {
  if (nameFilter === 'payState.retroActivityCalculationCount') {
    if (valueFilter === 'yes') {
      actualOperator = '=gt=';
      actualValue = '0';
    } else {
      actualOperator = '==';
      actualValue = '0';
    }
  }
  return {actualOperator, actualValue, nameFilter};
};

const payStateStatusToOngoingPayrollsFn = (nameFilter: string,
                                           valueFilter: string,
                                           actualOperator: string,
                                           actualValue: string | Array<Expression>) => {
  if (nameFilter === 'payState.status') {
    [actualValue] = [valueFilter].map(val => {
      if (val === 'ONGOING') {
        actualOperator = ',';
        return ['ONGOING', 'PAYSLIP_IN_PROGRESS', 'CALCULATION_DONE', 'STORAGE_IN_PROGRESS'].map(value => {
          return <Expression>{
            name: nameFilter,
            value,
            operator: '=='
          };
        });
      } else if (val === 'SCHEDULED') {
        actualOperator = ',';
        return ['SCHEDULED', 'SENDED'].map(value => {
          return <Expression>{
            name: nameFilter,
            value,
            operator: '=='
          };
        });
      }
      return actualValue;
    });
  }
  return {actualValue, actualOperator, nameFilter};
};

const actionToOngoingPayrollsFn = (nameFilter: string,
                                   valueFilter: string,
                                   actualOperator: string,
                                   actualValue: string | Array<Expression>) => {
  if (nameFilter === 'action') {
    [actualValue] = [valueFilter].map(val => {
      if (val === 'LAUNCH') {
        actualOperator = ',';
        return ['TODO', 'AGAIN', 'READY', 'DONE', 'ALERT', 'ERROR'].map(value => {
          return <Expression>{
            name: 'payState.status',
            value,
            operator: '=='
          };
        });
      } else if (val === 'DONE') {
        actualOperator = ',';
        return ['ALERT', 'AGAIN'].map(value => {
          return <Expression>{
            name: 'payState.status',
            value,
            operator: '=='
          };
        });
      } else if (val === 'FREEZE') {
        actualOperator = ',';
        return ['DONE'].map(value => {
          return <Expression>{
            name: 'payState.status',
            value,
            operator: '=='
          };
        });
      } else if (val === 'UNFREEZE') {
        actualOperator = ',';
        return ['FROZEN'].map(value => {
          return <Expression>{
            name: 'payState.status',
            value,
            operator: '=='
          };
        });
      }
      return actualValue;
    });
  }
  return {actualValue, actualOperator, nameFilter};
};

const payStateStatusToValidationPayslipsFn = (nameFilter: string,
                                              valueFilter: string,
                                              actualOperator: string,
                                              actualValue: string | Array<Expression>) => {
  if (nameFilter === 'payState.status') {
    [actualValue] = [valueFilter].map(status => {
      if (status === 'ONGOING') {
        actualOperator = ',';
        return ['ONGOING', 'SENDED', 'PAYSLIP_IN_PROGRESS', 'CALCULATION_DONE', 'STORAGE_IN_PROGRESS'].map(value => {
          return <Expression>{
            name: nameFilter,
            value,
            operator: '=='
          };
        });
      }
      return actualValue;
    });
  }
  return {actualValue, actualOperator, nameFilter};
};

const nameFn = (nameFilter: string,
                valueFilter: string,
                actualOperator: string,
                actualValue: string | Array<Expression>) => {
  if (nameFilter === 'name') {
    const fields = ['firstName', 'lastName'];
    actualOperator = ',';
    actualValue = valueFilter && valueFilter.split(' ')
      .filter(str => !isEmpty(str))
      .flatMap(value => fields.map(name => ({
        name,
        value: `*${value}*`,
        operator: '=='
      })));
    nameFilter = fields.join('|');
  }
  return {nameFilter, actualOperator, actualValue};
};

const idFn = (nameFilter: string,
                valueFilter: string,
                actualOperator: string,
                actualValue: string | Array<Expression>) => {
  if (nameFilter === 'externalNumber'
    || nameFilter === 'externalID'
    || nameFilter === 'id') {
    actualValue = `*${valueFilter}*`;
  }
  return {nameFilter, actualOperator, actualValue};
};

/**
 * Update filters and return a boolean if update should be done
 * @param valueFilter
 * @param filters
 * @param nameFilter
 * @param operator
 * @param listFilterFunctions
 * @param allowedChars
 */
const updateFilters = (valueFilter: string,
                       filters: Array<Expression>,
                       nameFilter: string,
                       operator: string,
                       listFilterFunctions: FilterFunction[],
                       allowedChars?: RegExp): boolean => {
  if (allowedChars && !allowedChars.test(valueFilter)) {
    valueFilter = null;
  }

  let actualValue: string | Array<Expression> = valueFilter,
    actualOperator = operator;

  listFilterFunctions.forEach(filterFn => {
    const result = filterFn(nameFilter, valueFilter, actualOperator, actualValue);
    actualOperator = result.actualOperator;
    actualValue = result.actualValue;
    nameFilter = result.nameFilter;
  });

  // Then add the new one or replace existing one if its valueFilter is not empty
  const existingElementIndex = filters.findIndex(currentFilter => currentFilter.name === nameFilter),
    newFilterValue = {
      name: nameFilter,
      value: actualValue,
      operator: actualOperator
    };

  if (existingElementIndex === -1 && !isEmpty(valueFilter)) {
    filters.push(newFilterValue);
  }

  if (existingElementIndex !== -1) {
    if (isEmpty(valueFilter)) {
      filters.splice(existingElementIndex, 1);
    } else {
      const noChanges = isEqual(newFilterValue, filters[existingElementIndex]);
      if (noChanges) {
        return false;
      } else {
        filters[existingElementIndex] = newFilterValue;
      }
    }
  }
  return true;
};

export const getFilterName = (key: string): string => {
  let result: string;

  switch (key) {
    case 'contractPeriodId':
      result = 'contractPeriod.id';
      break;
    case 'status':
      result = 'payState.status';
      break;
    case 'retroActivityCalculationCount':
      result = 'payState.retroActivityCalculationCount';
      break;
    default:
      result = key;
      break;
  }

  return result;
};

export const createFiltersObj = (filtersGroup: UntypedFormGroup): { [key: string]: string } => {
  const filters: { [key: string]: string } = filtersGroup.value;
  return Object.keys(filters)
    .reduce((obj: { [key: string]: string }, key: string) => {
      if (filters[key] !== '') {
        obj[key] = filters[key];
      }
      return obj;
    }, {});
};
